import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Card, Table, Input, Typography, Space, Tooltip } from 'antd';
import { GiftFilled } from '@ant-design/icons';
import { formatCurrency2SigFig } from '../../../core/utils/campaigns';
import './styles.css';

const { Title, Text } = Typography;

const CampaignRequestorReviewBudget = ({ formValues, onBudgetChange }) => {
  const { campaigns = [], budget: totalBudget = 0 } = formValues || {};
  const [editableCampaigns, setEditableCampaigns] = useState([]);

  // Initialize editable campaigns data with hierarchical structure
  useEffect(() => {
    const initialData = [];

    campaigns.forEach((campaign, campaignIndex) => {
      const lineItems = campaign.lineItems || [];
      const campaignBudget = campaign.budget || 0;

      // Add campaign header row
      initialData.push({
        key: `campaign-${campaignIndex}`,
        isCampaignHeader: true,
        campaignIndex,
        name: campaign.name || `Campaign ${campaignIndex + 1}`,
        type: campaign.type,
        budget: campaignBudget,
        lineItemCount: lineItems.length,
        // Calculate campaign totals
        totalImpressions: lineItems.reduce((sum, item) => sum + (item.impressions || 0), 0),
        totalBudget: lineItems.reduce((sum, item) => sum + (parseFloat(item.budgetTotal) || 0), 0),
      });

      // Add line item rows under this campaign
      lineItems.forEach((lineItem, lineItemIndex) => {
        const budgetTotal = lineItem.budgetTotal || 0;
        // Calculate percentage against campaign budget
        const budgetPercentage = campaignBudget > 0 && budgetTotal > 0
          ? (budgetTotal / campaignBudget * 100).toFixed(1)
          : lineItem.budgetPercentage || 0;

        initialData.push({
          key: `campaign-${campaignIndex}-lineitem-${lineItemIndex}`,
          isLineItem: true,
          campaignIndex,
          lineItemIndex,
          index: lineItemIndex + 1,
          name: lineItem.name || `Line Item ${lineItemIndex + 1}`,
          startDate: lineItem.startDate,
          endDate: lineItem.endDate,
          impressions: lineItem.impressions || 0,
          budgetTotal,
          budgetPercentage,
          valueAdd: lineItem.valueAdd,
          lineItemId: lineItem.id,
          ...lineItem
        });
      });
    });

    setEditableCampaigns(initialData);
  }, [campaigns, totalBudget]);

  // Handle budget change for both campaign headers and line items
  const handleBudgetChange = (tableRowIndex, field, value) => {
    const updatedData = [...editableCampaigns];
    const row = updatedData[tableRowIndex];

    // Handle campaign budget change
    if (row.isCampaignHeader && field === 'budget') {
      const newCampaignBudget = parseFloat(value) || 0;

      // Update campaign header budget
      updatedData[tableRowIndex] = {
        ...updatedData[tableRowIndex],
        budget: newCampaignBudget,
      };

      // Recalculate all line item percentages in this campaign based on new campaign budget
      const lineItemsInCampaign = updatedData
        .map((item, index) => ({ ...item, originalIndex: index }))
        .filter((item) => item.isLineItem && item.campaignIndex === row.campaignIndex);

      lineItemsInCampaign.forEach((lineItem) => {
        const lineItemBudget = parseFloat(lineItem.budgetTotal) || 0;
        const newPercentage = newCampaignBudget > 0
          ? (lineItemBudget / newCampaignBudget * 100).toFixed(1)
          : 0;

        updatedData[lineItem.originalIndex] = {
          ...updatedData[lineItem.originalIndex],
          budgetPercentage: newPercentage,
        };
      });

      setEditableCampaigns(updatedData);

      // Notify parent component - reconstruct campaigns structure
      if (onBudgetChange) {
        const reconstructedCampaigns = campaigns.map((campaign, campaignIndex) => {
          const lineItems = updatedData
            .filter(r => r.isLineItem && r.campaignIndex === campaignIndex)
            .map(r => ({
              ...r,
              budgetTotal: r.budgetTotal,
              budgetPercentage: r.budgetPercentage,
            }));

          // Update campaign budget if this is the campaign being edited
          const campaignBudget = campaignIndex === row.campaignIndex
            ? newCampaignBudget
            : campaign.budget;

          return {
            ...campaign,
            budget: campaignBudget,
            lineItems,
          };
        });

        onBudgetChange(reconstructedCampaigns);
      }
      return;
    }

    // Handle line item budget change (existing logic)
    if (!row.isLineItem) return;

    // Find the campaign budget for percentage calculation
    const campaignHeaderIndex = updatedData.findIndex(
      (item) => item.isCampaignHeader && item.campaignIndex === row.campaignIndex
    );
    const campaignBudget = campaignHeaderIndex >= 0
      ? updatedData[campaignHeaderIndex].budget
      : 0;

    // Update the line item
    updatedData[tableRowIndex] = {
      ...updatedData[tableRowIndex],
      [field]: value
    };

    // If budget total changes, recalculate percentage against campaign budget
    if (field === 'budgetTotal' && campaignBudget > 0) {
      const percentage = (parseFloat(value) / campaignBudget * 100).toFixed(1);
      updatedData[tableRowIndex].budgetPercentage = percentage;
    }

    // If percentage changes, recalculate budget total against campaign budget
    if (field === 'budgetPercentage' && campaignBudget > 0) {
      const budgetAmount = (campaignBudget * parseFloat(value) / 100).toFixed(2);
      updatedData[tableRowIndex].budgetTotal = budgetAmount;
    }

    // Recalculate campaign header totals
    if (campaignHeaderIndex >= 0) {
      const lineItemsInCampaign = updatedData.filter(
        (item) => item.isLineItem && item.campaignIndex === row.campaignIndex
      );

      updatedData[campaignHeaderIndex] = {
        ...updatedData[campaignHeaderIndex],
        totalImpressions: lineItemsInCampaign.reduce((sum, item) => sum + (item.impressions || 0), 0),
        totalBudget: lineItemsInCampaign.reduce((sum, item) => sum + (parseFloat(item.budgetTotal) || 0), 0),
      };
    }

    setEditableCampaigns(updatedData);

    // Notify parent component - reconstruct campaigns structure
    if (onBudgetChange) {
      const reconstructedCampaigns = campaigns.map((campaign, campaignIndex) => {
        const lineItems = updatedData
          .filter(r => r.isLineItem && r.campaignIndex === campaignIndex)
          .map(r => ({
            ...r,
            budgetTotal: r.budgetTotal,
            budgetPercentage: r.budgetPercentage,
          }));

        return {
          ...campaign,
          lineItems,
        };
      });

      onBudgetChange(reconstructedCampaigns);
    }
  };

  // Campaign type labels
  const campaignTypeLabels = {
    1: 'Display',
    7: 'External',
    8: 'Video',
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: '8%',
      render: (_, record) => {
        if (record.isCampaignHeader) {
          return null;
        }
        return (
          <Text style={{ fontSize: '14px', paddingLeft: '16px' }}>
            {record.index}
          </Text>
        );
      },
    },
    {
      title: 'Campaign / Line Item',
      key: 'name',
      width: '25%',
      render: (_, record) => {
        if (record.isCampaignHeader) {
          return (
            <Space>
              <Text strong style={{ fontSize: '15px' }}>
                {record.name}
              </Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                ({campaignTypeLabels[record.type] || 'Display'})
              </Text>
            </Space>
          );
        }
        return (
          <Text style={{ fontSize: '14px', paddingLeft: '16px' }}>
            {record.name}
          </Text>
        );
      },
    },
    {
      title: 'Duration',
      key: 'duration',
      width: '20%',
      render: (_, record) => {
        if (record.isCampaignHeader) {
          return (
            <Text type="secondary" style={{ fontSize: '13px' }}>
              {record.lineItemCount} Line Item{record.lineItemCount !== 1 ? 's' : ''}
            </Text>
          );
        }
        return (
          <Text style={{ fontSize: '14px' }}>
            {record.startDate && record.endDate
              ? `${moment(record.startDate).format('MM/DD/YYYY')} - ${moment(record.endDate).format('MM/DD/YYYY')}`
              : 'Not specified'
            }
          </Text>
        );
      },
    },
    {
      title: 'Imps.',
      key: 'impressions',
      width: '12%',
      render: (_, record) => {
        if (record.isCampaignHeader) {
          return (
            <Text strong style={{ fontSize: '14px' }}>
              {(record.totalImpressions || 0).toLocaleString()}
            </Text>
          );
        }
        return (
          <Text style={{ fontSize: '14px' }}>
            {(record.impressions || 0).toLocaleString()}
          </Text>
        );
      },
    },
    {
      title: 'Budget',
      key: 'budget',
      width: '20%',
      render: (_, record, index) => {
        if (record.isCampaignHeader) {
          const utilization = record.budget > 0
            ? Math.round((record.totalBudget / record.budget) * 100)
            : 0;
          return (
            <Space direction="vertical" size={2}>
              <Input
                value={record.budget}
                onChange={(e) => handleBudgetChange(index, 'budget', e.target.value)}
                prefix="$"
                style={{ width: '120px', fontWeight: 600 }}
                size="small"
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {utilization}% allocated
              </Text>
            </Space>
          );
        }
        return (
          <Space size={4}>
            <Input
              value={record.budgetTotal}
              onChange={(e) => handleBudgetChange(index, 'budgetTotal', e.target.value)}
              prefix="$"
              style={{ width: '80px' }}
              size="small"
            />
            <Input
              value={record.budgetPercentage}
              onChange={(e) => handleBudgetChange(index, 'budgetPercentage', e.target.value)}
              suffix="%"
              style={{ width: '60px' }}
              size="small"
            />
          </Space>
        );
      },
    },
    {
      title: 'Value Added',
      key: 'valueAdded',
      width: '15%',
      align: 'center',
      render: (_, record) => {
        if (record.isCampaignHeader) {
          return null;
        }
        return record.valueAdd ? (
          <Tooltip title="Yes">
            <GiftFilled
              style={{
                fontSize: 20,
                color: '#722ed1',
              }}
            />
          </Tooltip>
        ) : null;
      },
    },
  ];

  // Calculate totals - only count line items, not campaign headers
  const lineItemRows = editableCampaigns.filter(row => row.isLineItem);
  const totalLineItems = lineItemRows.length;
  const totalImpressions = lineItemRows.reduce((sum, item) => sum + (item.impressions || 0), 0);
  const totalBudgetUsed = lineItemRows.reduce((sum, item) => sum + (parseFloat(item.budgetTotal) || 0), 0);

  return (
    <Card>
      <div className="requester-table-wrapper">
        <div className="requester-table-header">
          <Title level={5}>BUDGET ALLOCATION</Title>
        </div>

        <Table
          className="requester-table"
          columns={columns}
          dataSource={editableCampaigns}
          pagination={false}
          size="small"
          rowClassName={(record) => {
            if (record.isCampaignHeader) {
              return 'campaign-header-row';
            }
            return '';
          }}
          footer={() => (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
              <Text strong>
                {totalLineItems} Line Item{totalLineItems !== 1 ? 's' : ''}
              </Text>
              <Space size="large">
                <Text>
                  <Text type="secondary">Total Impressions: </Text>
                  <Text strong>{totalImpressions.toLocaleString()}</Text>
                </Text>
                <Text>
                  <Text type="secondary">Budget Used: </Text>
                  <Text strong>{formatCurrency2SigFig(totalBudgetUsed)} / {formatCurrency2SigFig(totalBudget)}</Text>
                </Text>
              </Space>
            </div>
          )}
        />
      </div>
    </Card>
  );
};

export default CampaignRequestorReviewBudget;